import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PackageX } from 'lucide-react';
import api from '../../lib/api';
import { Textarea } from '../../components/ui/textarea';
import { ConfirmButton } from '../StaffAppItemDetail';
import ScanOrType from './ScanOrType';
import ReceivingLineCard, { lineResult } from './ReceivingLineCard';
import ClaimNoteSheetButton from './ClaimNoteSheetButton';
import ToolHeader, { Spinner } from './ToolHeader';

const blank = { delivered: 0, batch_number: '', expiry_date: '', damaged: '' };

export default function ReceivingCheckPage() {
  const { poId } = useParams();
  const { tools } = useOutletContext();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState({});
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(null);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    api.get(`/staff-app/receiving/orders/${poId}`).then(({ data }) => {
      setOrder(data.order);
      setLatest(data.latest_precheck);
      setLines(Object.fromEntries(data.order.items.map((i) => [i.product_id, { ...blank }])));
    }).catch(() => toast.error('Could not load this order'));
  }, [poId]);

  const summary = useMemo(() => {
    const s = { match: 0, short: 0, over: 0, missing: 0 };
    (order?.items || []).forEach((i) => { s[lineResult(i.outstanding, parseInt(lines[i.product_id]?.delivered, 10) || 0)] += 1; });
    return s;
  }, [order, lines]);

  const damagedCount = Object.values(lines).filter((l) => (parseInt(l.damaged, 10) || 0) > 0).length;
  if (!order) return <><ToolHeader title="Delivery check" back="/staff-app/tools/receiving" /><Spinner /></>;

  const bump = (pid) => {
    setLines((ls) => ({ ...ls, [pid]: { ...ls[pid], delivered: (parseInt(ls[pid].delivered, 10) || 0) + 1 } }));
    setFlash(pid);
    setTimeout(() => setFlash(''), 900);
    document.getElementById(`po-line-${pid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onCode = async (code) => {
    const byBarcode = order.items.find((i) => i.barcode && i.barcode === code);
    if (byBarcode) return bump(byBarcode.product_id);
    try {
      const { data } = await api.get('/staff-app/products/lookup', { params: { q: code } });
      const hit = order.items.find((i) => data.products.some((p) => p.product_id === i.product_id));
      if (hit) return bump(hit.product_id);
      const p = data.exact ? data.products[0] : null;
      setExtras((xs) => {
        const k = xs.findIndex((x) => x.code === code);
        if (k >= 0) return xs.map((x, j) => (j === k ? { ...x, quantity: x.quantity + 1 } : x));
        return [...xs, { code, product_id: p?.product_id || '', product_name: p?.name || 'Unknown item', quantity: 1 }];
      });
      toast.warning(`${p?.name || code} is not on this order`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Lookup failed'); }
  };

  const savePrecheck = async () => {
    setBusy(true);
    try {
      const payload = {
        notes, unexpected: extras,
        lines: order.items.map((i) => ({ product_id: i.product_id, quantity_delivered: parseInt(lines[i.product_id].delivered, 10) || 0,
          batch_number: lines[i.product_id].batch_number, expiry_date: lines[i.product_id].expiry_date, damaged: parseInt(lines[i.product_id].damaged, 10) || 0 })),
      };
      const { data } = await api.post(`/staff-app/receiving/orders/${poId}/precheck`, payload);
      setSaved(data);
      setLatest(data);
      toast.success(data.status === 'clean' ? 'Pre-check saved — everything matches' : `Pre-check saved with ${data.issue_count} issue(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save pre-check'); } finally { setBusy(false); }
  };

  const receive = async () => {
    setBusy(true);
    try {
      const items = saved.lines.map((l) => ({ product_id: l.product_id, quantity_received: Math.max(0, l.delivered - l.damaged), batch_number: l.batch_number, expiry_date: l.expiry_date }))
        .filter((l) => l.quantity_received > 0);
      if (!items.length) throw new Error('Nothing usable to receive');
      await api.post(`/purchase-orders/${poId}/receive`, { items });
      toast.success('Goods received into stock');
      navigate('/staff-app/tools/receiving');
    } catch (err) { toast.error(err.response?.data?.detail || err.message || 'Could not receive goods'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="staff-app-receiving-check">
      <ToolHeader title={order.supplier_name || 'Delivery check'} subtitle={order.po_id} back="/staff-app/tools/receiving" />
      <div className="sticky top-[57px] z-10 bg-background/95 backdrop-blur px-4 pt-3 pb-2 space-y-2 border-b border-border">
        <ScanOrType onCode={onCode} continuous testId="staff-app-receiving-scan" />
        <div className="grid grid-cols-4 gap-1.5 text-center text-[11px] font-semibold" data-testid="staff-app-receiving-summary">
          {Object.entries(summary).map(([k, v]) => <span key={k} className="rounded-md bg-muted py-1 capitalize">{k} {v}</span>)}
        </div>
      </div>
      <div className="p-4 space-y-2.5">
        {order.items.map((i) => (
          <ReceivingLineCard key={i.product_id} item={i} line={lines[i.product_id]} highlight={flash === i.product_id}
            onChange={(l) => { setLines({ ...lines, [i.product_id]: l }); setSaved(null); }} />
        ))}
        {extras.length > 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-1.5" data-testid="staff-app-receiving-unexpected">
            <p className="text-xs font-bold flex items-center gap-1.5"><PackageX className="w-4 h-4" /> Not on this order</p>
            {extras.map((x) => <p key={x.code} className="text-xs">{x.product_name} ({x.code}) × {x.quantity}</p>)}
          </div>
        )}
        <Textarea value={notes} onChange={(e) => { setNotes(e.target.value); setSaved(null); }} placeholder="Notes for the buyer (optional)" rows={2} data-testid="staff-app-receiving-notes" />
        <div className="flex gap-2 pt-1">
          <ConfirmButton label="Save pre-check" title="Save this delivery pre-check?" testId="staff-app-receiving-save" onConfirm={savePrecheck} loading={busy && !saved}
            description={`${summary.match} matching, ${summary.short + summary.missing} short/missing, ${summary.over} over, ${damagedCount} with damage, ${extras.length} unexpected. Stock is not changed by a pre-check.`} />
          {tools?.receiving_post && saved && (
            <ConfirmButton label="Receive into stock" title="Receive these goods into stock?" testId="staff-app-receiving-post" onConfirm={receive} loading={busy}
              description="Usable quantities (delivered minus damaged) will be added to stock with the batch and expiry entered. This can't be undone from the app." />
          )}
        </div>
        <ClaimNoteSheetButton precheck={latest} />
        {saved && !tools?.receiving_post && <p className="text-xs text-muted-foreground" data-testid="staff-app-receiving-saved-note">Pre-check saved. A purchasing manager will post the receipt.</p>}
      </div>
    </div>
  );
}
